import { Badge } from '../../../components/ui';
import { useT } from '../../../i18n';
import type { BoDich } from '../../../i18n';
import { KHONG_CO, ngayVN, soVN } from '../../../lib/dinh-dang';
import { BADGE_HIEU_LUC, BADGE_KET, BADGE_KHONG_AI_DOC, TRANG_THAI_NAP } from '../constants';
import { ngayHomNay, tinhHieuLuc } from '../nghiep-vu/tri-thuc';
import type { KnowledgeFile, TomTatQuyenDoc } from '../types';

/** Ô "Hiệu lực": khoảng ngày, kèm nhãn khi hôm nay nằm ngoài khoảng đó. */
export function OHieuLuc({ tep }: { tep: Pick<KnowledgeFile, 'effective_from' | 'effective_to'> }) {
  const { t } = useT();
  const tinhTrang = tinhHieuLuc(tep.effective_from, tep.effective_to, ngayHomNay());
  if (tinhTrang === 'khongDat') return <span className="text-xs text-content-muted">{KHONG_CO}</span>;
  const nhan = tinhTrang === 'het' || tinhTrang === 'chuaToi' ? BADGE_HIEU_LUC[tinhTrang] : undefined;
  return (
    <div className="flex flex-col items-start gap-1">
      <span className="whitespace-nowrap text-xs text-content-secondary">
        {t('triThuc.tep.khoangHieuLuc', {
          tu: tep.effective_from ? ngayVN(tep.effective_from) : KHONG_CO,
          den: tep.effective_to ? ngayVN(tep.effective_to) : t('triThuc.tep.khongThoiHan'),
        })}
      </span>
      {nhan && <Badge variant={nhan.variant}>{t(nhan.khoa)}</Badge>}
    </div>
  );
}

/**
 * Ô "Trạng thái nạp": nhãn của hàng đợi trong module, cộng nhãn "Kẹt" khi tệp chờ quá hạn, và câu lỗi hay
 * cảnh báo index trả về (nguyên văn, không dịch — ADR-UI-0010 mục 5).
 */
export function OTrangThaiNap({ tep }: { tep: KnowledgeFile }) {
  const { t, locale } = useT();
  const cauHinh = TRANG_THAI_NAP[tep.index_status];
  const phu = !tep.has_file
    ? t('triThuc.nap.chiThongTin')
    : tep.index_status === 'LOI' && tep.index_attempts > 1
      ? t('triThuc.nap.soLanThu', { so: soVN(tep.index_attempts, locale) })
      : null;
  return (
    <div className="flex min-w-0 max-w-56 flex-col items-start gap-1">
      <span className="flex flex-wrap items-center gap-1">
        <Badge variant={cauHinh.variant}>{t(cauHinh.khoa)}</Badge>
        {tep.index_stuck && <Badge variant={BADGE_KET.variant}>{t(BADGE_KET.khoa)}</Badge>}
      </span>
      {tep.index_error && (
        <span className="line-clamp-2 break-words text-2xs text-danger-700" title={tep.index_error}>
          {tep.index_error}
        </span>
      )}
      {tep.index_warning && (
        <span className="line-clamp-2 break-words text-2xs text-warning-700" title={tep.index_warning}>
          {tep.index_warning}
        </span>
      )}
      {phu && <span className="text-2xs text-content-muted">{phu}</span>}
    </div>
  );
}

/** Ô "Phân quyền": một dòng cho cả tệp và thư mục, nói ai đang quyết định quyền đọc. */
export function OPhanQuyen({ tomTat }: { tomTat: TomTatQuyenDoc }) {
  const { t, locale } = useT();
  if (!tomTat.inherit && tomTat.department_count === 0 && tomTat.user_count === 0) {
    return <Badge variant={BADGE_KHONG_AI_DOC.variant}>{t(BADGE_KHONG_AI_DOC.khoa)}</Badge>;
  }
  let nhan: string;
  if (!tomTat.inherit) nhan = demQuyen(t, locale, tomTat.department_count, tomTat.user_count);
  else if (tomTat.source_type !== 'FOLDER') nhan = t('triThuc.quyen.theoBo');
  else nhan = tomTat.source_name ? t('triThuc.quyen.theoThuMuc', { ten: tomTat.source_name }) : t('triThuc.quyen.theoThuMucKhongTen');
  return (
    <span className="block min-w-0 max-w-40 truncate text-xs text-content-secondary" title={nhan}>
      {nhan}
    </span>
  );
}

/** "3 phòng ban · 2 người", bỏ vế bằng 0; cả hai bằng 0 thì "0 người". */
export function demQuyen(t: BoDich['t'], locale: string, soPhong: number, soNguoi: number): string {
  const phong = soPhong === 1 ? t('triThuc.quyen.motPhongBan') : t('triThuc.quyen.soPhongBan', { so: soVN(soPhong, locale) });
  const nguoi = soNguoi === 1 ? t('triThuc.quyen.motNguoi') : t('triThuc.quyen.soNguoi', { so: soVN(soNguoi, locale) });
  if (soPhong === 0) return nguoi;
  return soNguoi === 0 ? phong : `${phong} · ${nguoi}`;
}
